/**
 * Utility functions for JWT token handling
 */

/**
 * Decode a JWT token payload
 * @param {string} token - The JWT token
 * @returns {Object|null} - The decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  try {
    if (!token) return null;
    
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Get user authorities from JWT token
 * @param {string} token - The JWT token
 * @returns {Array} - Array of user authorities
 */
export const getUserAuthorities = (token) => {
  const decodedToken = decodeToken(token);
  if (!decodedToken) return [];
  
  return decodedToken.authorities || [];
};

/**
 * Check if user has a specific authority
 * @param {string} token - The JWT token
 * @param {string} authority - The authority to check
 * @returns {boolean} - True if user has the authority
 */
export const hasAuthority = (token, authority) => {
  const authorities = getUserAuthorities(token);
  return authorities.includes(authority);
};

/**
 * Check if user is an admin
 * @param {string} token - The JWT token
 * @returns {boolean} - True if user is an admin
 */
export const isAdmin = (token) => {
  return hasAuthority(token, 'ADMIN');
};

/**
 * Get user information from token
 * @param {string} token - The JWT token
 * @returns {Object} - User information
 */
export const getUserInfo = (token) => {
  const decodedToken = decodeToken(token);
  if (!decodedToken) return {};
  
  return {
    username: decodedToken.user_name || '',
    authorities: decodedToken.authorities || [],
    clientId: decodedToken.client_id || '',
    scope: decodedToken.scope || []
  };
};

export default {
  decodeToken,
  getUserAuthorities,
  hasAuthority,
  isAdmin,
  getUserInfo
};